import { useCallback, useEffect, useRef, useState } from "react"

export const TwoThumbSlider = ({
    min,
    max,
    selectedMin,
    selectedMax,
    setSelectedMin,
    setSelectedMax,
    isDisabled,
}: {
    min: number
    max: number
    selectedMin: number
    selectedMax: number
    setSelectedMin: (min: number) => void
    setSelectedMax: (max: number) => void
    isDisabled?: boolean
}) => {
    const [localMin, setLocalMin] = useState(selectedMin)
    const [localMax, setLocalMax] = useState(selectedMax)

    useEffect(() => {
        setLocalMin(selectedMin)
    }, [selectedMin])

    useEffect(() => {
        setLocalMax(selectedMax)
    }, [selectedMax])

    const getPercent = (value: number) => ((value - min) / (max - min)) * 100
    const minPercent = getPercent(localMin)
    const maxPercent = getPercent(localMax)

    return (
        <div className="relative w-full -translate-y-1.5">
            <Thumb
                min={min}
                max={max}
                value={localMin}
                onChange={(value) => {
                    const min = Math.min(value, localMax - 1)
                    setLocalMin(min)
                    setSelectedMin(min)
                }}
                isDisabled={isDisabled}
            />
            <Thumb
                min={min}
                max={max}
                value={localMax}
                onChange={(value) => {
                    const max = Math.max(value, localMin + 1)
                    setLocalMax(max)
                    setSelectedMax(max)
                }}
                isDisabled={isDisabled}
            />

            <div
                className="absolute top-[0.4375rem] -translate-y-1/2 h-0.5 bg-red-700 z-[1] rounded-full"
                style={{
                    left: `${minPercent}%`,
                    width: `${maxPercent - minPercent}%`,
                }}
            />
        </div>
    )
}

const Thumb = ({
    min,
    max,
    value,
    onChange,
    isDisabled,
}: {
    min: number
    max: number
    value: number
    onChange: (value: number) => void
    isDisabled?: boolean
}) => {
    const [isHovering, setIsHovering] = useState(false)
    const [isHoveringThumb, setIsHoveringThumb] = useState(false)
    const [isActive, setIsActive] = useState(false)

    useEffect(() => {
        if (isActive) {
            inputRef.current?.style.setProperty("--thumb-color", "#27bbff") // blue-500
            inputRef.current?.style.setProperty("--thumb-border", "transparent")
        } else if (isHoveringThumb) {
            inputRef.current?.style.setProperty("--thumb-color", "#27bbff") // blue-500
            inputRef.current?.style.setProperty("--thumb-border", "#ffffff") // gray-0
        } else {
            inputRef.current?.style.setProperty("--thumb-color", null)
            inputRef.current?.style.setProperty("--thumb-border", null)
        }
    }, [isHoveringThumb, isActive])

    useEffect(() => {
        if (!isHovering) setIsHoveringThumb(false)
    }, [isHovering])

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            if (!isHovering || !inputRef.current) return
            const rect = inputRef.current?.getBoundingClientRect()
            const min = Number(inputRef.current.min)
            const max = Number(inputRef.current.max)
            const value = Number(inputRef.current.value)
            const percent = (value - min) / (max - min)
            const thumbLeft = rect.left + percent * (rect.width - 14)
            const thumbRight = thumbLeft + 14
            setIsHoveringThumb(e.clientX >= thumbLeft && e.clientX <= thumbRight)
        },
        [isHovering]
    )

    const inputRef = useRef<HTMLInputElement>(null)

    return (
        <input
            disabled={isDisabled}
            ref={inputRef}
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            onMouseMove={handleMouseMove}
            className="two-slider"
        />
    )
}
